
import React, { useEffect, useState, useRef } from 'react';
import { Card, Badge, Button } from '../components/UI';
import { Clock, Calendar, AlertTriangle, Settings, History, Flag, Trophy, Users, TrendingUp, Newspaper, ArrowRight, Zap, Target, Skull, Star, CheckCircle, Vote, ChevronDown, ChevronUp, Tv, BookOpen, Bell, X, FlaskConical, Swords, Timer, BarChart2, XCircle, Activity, Radar, MessageSquare, Info, RefreshCw, Megaphone, UserCheck, UserMinus, UserX, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { UserRole, NewsItem, TicketStatus, DotDCandidate, AttendanceStatus } from '../types';
import { API_BASE_URL } from '../config';

type SeasonSignupStatus = 'MAIN' | 'RESERVE' | 'OUT';

interface SeasonSignupDashboardData {
  campaign: null | {
    id: number;
    season_name: string;
    deadline?: string | null;
    is_active: boolean;
  };
  response: SeasonSignupStatus | null;
  counts: Record<SeasonSignupStatus | 'PENDING', number>;
}

// ... (Existing Widgets remain unchanged: FormCurveWidget, TeamRadarWidget, PollWidget, DotDWidget, AdvancedTeammateBattle, LiveStreamWidget)

// --- NEW: FORM CURVE WIDGET ---
const FormCurveWidget: React.FC<{ data: any[] }> = ({ data }) => {
    const displayData = (data && data.length > 0) 
        ? data 
        : Array(5).fill({ status: 'PENDING', position: '-', location: '?' });
    
    const hasData = data && data.length > 0;
    
    // Calculate chart coordinates for the position line
    const chartH = 90;
    const chartW = 220;
    const padX = 30;
    const padTop = 10;
    const padBot = 20;
    const maxPos = 20;
    const minPos = 1;

    const getY = (pos: number) => {
        const clamped = Math.max(minPos, Math.min(maxPos, pos));
        return padTop + ((clamped - minPos) / (maxPos - minPos)) * (chartH - padTop - padBot);
    };

    const points = displayData.map((res, i) => {
        const x = padX + (i / Math.max(displayData.length - 1, 1)) * (chartW - padX * 2);
        const pos = res.status === 'FINISHED' ? Number(res.position) : maxPos;
        const y = getY(pos);
        return { x, y, ...res };
    });

    // Build SVG path for smooth curve
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
    const areaPath = linePath + ` L${points[points.length-1].x},${chartH - padBot} L${points[0].x},${chartH - padBot} Z`;

    // Average position for trend indicator
    const finishedResults = displayData.filter(d => d.status === 'FINISHED');
    const avgPos = finishedResults.length > 0 ? (finishedResults.reduce((sum: number, d: any) => sum + Number(d.position), 0) / finishedResults.length).toFixed(1) : '-';
    
    // Trend: compare first half vs second half
    let trendIcon = '→';
    let trendColor = 'text-gray-400';
    if (finishedResults.length >= 3) {
        const mid = Math.floor(finishedResults.length / 2);
        const firstHalf = finishedResults.slice(0, mid).reduce((s: number, d: any) => s + Number(d.position), 0) / mid;
        const secondHalf = finishedResults.slice(mid).reduce((s: number, d: any) => s + Number(d.position), 0) / (finishedResults.length - mid);
        if (secondHalf < firstHalf - 0.5) { trendIcon = '↑'; trendColor = 'text-green-400'; }
        else if (secondHalf > firstHalf + 0.5) { trendIcon = '↓'; trendColor = 'text-red-400'; }
    }

    const getColor = (res: any) => {
        if (res.status !== 'FINISHED') return { main: '#ef4444', glow: 'rgba(239,68,68,0.4)' };
        const p = Number(res.position);
        if (p === 1) return { main: '#fbbf24', glow: 'rgba(251,191,36,0.5)' };
        if (p <= 3) return { main: '#22c55e', glow: 'rgba(34,197,94,0.4)' };
        if (p <= 10) return { main: '#3b82f6', glow: 'rgba(59,130,246,0.4)' };
        return { main: '#6b7280', glow: 'rgba(107,114,128,0.3)' };
    };

    return (
        <Card className="h-full flex flex-col relative overflow-hidden min-h-[230px] rounded-[1.75rem] bg-gradient-to-br from-[#151923] to-[#0f1119] border-white/[0.07]">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3 border-b border-gray-800/30 pb-2 z-10">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-green-500/20 to-green-600/5 flex items-center justify-center border border-green-500/20">
                    <TrendingUp size={14} className={hasData ? "text-green-400" : "text-gray-600"} />
                </div>
                <div>
                    <h3 className="font-bold text-white uppercase text-xs tracking-wider">Formkurve</h3>
                    <span className="text-[9px] text-gray-500">Letzte {displayData.length} Rennen</span>
                </div>
                {hasData && (
                    <div className="ml-auto flex items-center gap-3">
                        <div className="text-right">
                            <div className="text-[9px] text-gray-500 uppercase">Ø Pos.</div>
                            <div className="text-sm font-bold text-white font-mono leading-none">{avgPos}</div>
                        </div>
                        <div className={`text-lg font-bold ${trendColor} leading-none`}>{trendIcon}</div>
                    </div>
                )}
            </div>
            
            <div className={`flex-1 relative z-10 ${!hasData ? 'opacity-30 blur-[1px]' : ''}`}>
                {/* SVG Chart */}
                <svg width="100%" height="100%" viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="xMidYMid meet" className="overflow-visible">
                    <defs>
                        <linearGradient id="formGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.25" />
                            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                        </linearGradient>
                        <filter id="dotGlow">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                    </defs>

                    {/* Grid lines */}
                    {[1, 3, 10, 20].map(pos => (
                        <g key={pos}>
                            <line x1={padX - 5} y1={getY(pos)} x2={chartW - padX + 5} y2={getY(pos)} stroke="#333" strokeWidth="0.5" strokeDasharray={pos === 1 ? "0" : "2,3"} />
                            <text x={padX - 8} y={getY(pos) + 3} fill="#555" fontSize="7" textAnchor="end" fontFamily="monospace">P{pos}</text>
                        </g>
                    ))}

                    {/* Area fill under curve */}
                    {hasData && <path d={areaPath} fill="url(#formGrad)" />}

                    {/* Main line */}
                    {hasData && <path d={linePath} fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}

                    {/* Data points */}
                    {points.map((p, i) => {
                        const colors = getColor(p);
                        const label = p.status === 'FINISHED' ? `P${p.position}` : (p.status === 'DNF' || p.status === 'DSQ') ? p.status : '-';
                        return (
                            <g key={i}>
                                {/* Glow */}
                                <circle cx={p.x} cy={p.y} r="6" fill={colors.glow} />
                                {/* Outer ring */}
                                <circle cx={p.x} cy={p.y} r="4" fill="#111" stroke={colors.main} strokeWidth="1.5" />
                                {/* Inner dot */}
                                <circle cx={p.x} cy={p.y} r="1.5" fill={colors.main} />
                                {/* Label below */}
                                <text x={p.x} y={chartH - 4} fill={colors.main} fontSize="7" textAnchor="middle" fontWeight="bold" fontFamily="monospace">{label}</text>
                                {/* Location on top */}
                                {p.location && p.location !== '?' && (
                                    <text x={p.x} y={Math.max(p.y - 10, 8)} fill="#777" fontSize="5.5" textAnchor="middle">{p.location?.substring(0, 6)}</text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
            
            {!hasData && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <span className="text-xs font-bold text-gray-500 bg-[#0e0e16] px-3 py-1 rounded border border-gray-700">Keine Daten</span>
                </div>
            )}
        </Card>
    );
};

// --- UPDATED: TEAM RADAR WIDGET ---
const TeamRadarWidget: React.FC<{ teamsData: any[], userTeamId: string | null }> = ({ teamsData, userTeamId }) => {
    const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

    useEffect(() => {
        if (userTeamId && teamsData.length > 0) {
            setSelectedTeamId(userTeamId);
        } else if (teamsData.length > 0) {
            setSelectedTeamId(teamsData[0].id);
        }
    }, [userTeamId, teamsData]);

    const activeTeam = teamsData.find(t => t.id === selectedTeamId) || teamsData[0];
    const s = activeTeam ? activeTeam.stats : { pace: 50, reliability: 50, racecraft: 50, consistency: 50 };
    const teamColor = activeTeam ? activeTeam.color : '#666';
    const hasData = !!activeTeam;
    
    const cx = 80, cy = 80, maxR = 60;
    const categories = [
        { key: 'pace', label: 'Pace', angle: -90, icon: '⚡' },
        { key: 'reliability', label: 'Reliab.', angle: -18, icon: '🛡' },
        { key: 'racecraft', label: 'Racecraft', angle: 54, icon: '🏎' },
        { key: 'consistency', label: 'Consist.', angle: 126, icon: '📊' },
    ];
    // Add a 5th: overall average
    const overall = Math.round((s.pace + s.reliability + s.racecraft + s.consistency) / 4);
    const allCats = [...categories, { key: 'overall', label: 'Overall', angle: 198, icon: '⭐' }];
    const statsWithOverall = { ...s, overall };

    const getPoint = (value: number, angleDeg: number) => {
        const rad = (angleDeg * Math.PI) / 180;
        const r = (value / 100) * maxR;
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };

    // Pentagon grid rings
    const rings = [25, 50, 75, 100];
    const gridPaths = rings.map(pct => {
        const pts = allCats.map(c => getPoint(pct, c.angle));
        return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
    });

    // Data polygon
    const dataPoints = allCats.map(c => getPoint((statsWithOverall as any)[c.key], c.angle));
    const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';

    // Axis lines
    const axisEnds = allCats.map(c => getPoint(100, c.angle));

    // Stat bar helper
    const StatBar: React.FC<{label: string, value: number, icon: string}> = ({label, value, icon}) => (
        <div className="flex items-center gap-2 group">
            <span className="text-[10px] w-3 text-center">{icon}</span>
            <span className="text-[10px] text-gray-400 w-14 truncate">{label}</span>
            <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden relative">
                <div 
                    className="h-full rounded-full transition-all duration-700 ease-out" 
                    style={{ width: `${value}%`, backgroundColor: teamColor, boxShadow: `0 0 6px ${teamColor}40` }}
                />
            </div>
            <span className="text-[10px] font-mono text-white w-6 text-right font-bold">{value}</span>
        </div>
    );

    return (
        <Card className="relative overflow-hidden min-h-[230px] rounded-[1.75rem] bg-gradient-to-br from-[#151923] to-[#0f1119] border-white/[0.07]" style={{borderTop: `3px solid ${teamColor}`}}>
            {/* Subtle bg glow */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-[0.04]" style={{background: `radial-gradient(circle, ${teamColor}, transparent)`}} />

            {/* Header */}
            <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-800/30 pb-2 z-10 relative">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center border" style={{borderColor: teamColor + '40', background: teamColor + '15'}}>
                        <Radar size={14} style={{color: teamColor}} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white uppercase text-xs tracking-wider">Team-Radar</h3>
                        <span className="text-[9px] text-gray-500">Saison-Performance</span>
                    </div>
                </div>
                {teamsData.length > 0 && (
                    <select 
                        className="bg-[#0e0e16] border border-gray-800/40 text-[10px] text-white rounded px-2 py-1 max-w-[110px] focus:border-gray-500 outline-none"
                        value={selectedTeamId || ''}
                        onChange={(e) => setSelectedTeamId(e.target.value)}
                    >
                        {teamsData.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                )}
            </div>
            
            <div className={`flex flex-col sm:flex-row items-center gap-3 ${!hasData ? 'opacity-20 blur-[2px]' : ''}`}>
                {/* SVG Radar */}
                <div className="w-[150px] h-[150px] relative shrink-0">
                    <svg width="100%" height="100%" viewBox="0 0 160 160">
                        <defs>
                            <radialGradient id={`radarGrad-${selectedTeamId}`} cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor={teamColor} stopOpacity="0.35" />
                                <stop offset="100%" stopColor={teamColor} stopOpacity="0.05" />
                            </radialGradient>
                            <filter id="radarGlow">
                                <feGaussianBlur stdDeviation="2" result="blur" />
                                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                            </filter>
                        </defs>

                        {/* Grid rings */}
                        {gridPaths.map((path, i) => (
                            <path key={i} d={path} fill="none" stroke="#333" strokeWidth={i === rings.length - 1 ? "1" : "0.5"} strokeDasharray={i < rings.length - 1 ? "2,3" : "0"} />
                        ))}

                        {/* Axis lines */}
                        {axisEnds.map((p, i) => (
                            <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#333" strokeWidth="0.5" />
                        ))}

                        {/* Data fill */}
                        <path d={dataPath} fill={`url(#radarGrad-${selectedTeamId})`} stroke={teamColor} strokeWidth="2" strokeLinejoin="round" filter="url(#radarGlow)" />

                        {/* Data dots */}
                        {dataPoints.map((p, i) => (
                            <g key={i}>
                                <circle cx={p.x} cy={p.y} r="5" fill={teamColor} fillOpacity="0.2" />
                                <circle cx={p.x} cy={p.y} r="2.5" fill="#111" stroke={teamColor} strokeWidth="1.5" />
                            </g>
                        ))}

                        {/* Labels */}
                        {allCats.map((c, i) => {
                            const lp = getPoint(115, c.angle);
                            return (
                                <text key={i} x={lp.x} y={lp.y} fill="#888" fontSize="7" textAnchor="middle" dominantBaseline="middle" fontWeight="600">
                                    {c.label}
                                </text>
                            );
                        })}
                    </svg>
                </div>

                {/* Stats bars */}
                <div className="flex-1 w-full space-y-2">
                    <div className="text-center sm:text-left mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{color: teamColor}}>{activeTeam ? activeTeam.name : 'Kein Team'}</span>
                        <div className="flex items-center gap-1 justify-center sm:justify-start mt-0.5">
                            <span className="text-[9px] text-gray-500">Gesamtwertung:</span>
                            <span className="text-xs font-bold font-mono" style={{color: teamColor}}>{overall}/100</span>
                        </div>
                    </div>
                    
                    {allCats.map(c => (
                        <StatBar key={c.key} label={c.label} value={(statsWithOverall as any)[c.key]} icon={c.icon} />
                    ))}
                </div>
            </div>
            
            {!hasData && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <span className="text-xs font-bold text-gray-500 bg-[#0e0e16] px-3 py-1 rounded border border-gray-700">Keine Daten</span>
                </div>
            )}
        </Card>
    );
};

// ... (Rest of components: PollWidget, DotDWidget, AdvancedTeammateBattle, LiveStreamWidget, NotificationBell) remain unchanged ...

const PollWidget: React.FC<{ user: any, onUnvoted: (count: number) => void }> = ({ user, onUnvoted }) => {
    const [polls, setPolls] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadPolls(); }, []);

    const loadPolls = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const res = await apiRequest<any[]>(`polls.php?userId=${user.id}&_t=${Date.now()}`);
            const data = Array.isArray(res) ? res : [];
            setPolls(data);
            const unvoted = data.filter(p => p.status === 'OPEN' && !p.userVoted).length;
            onUnvoted(unvoted);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleVote = async (pollId: number, optionId: number) => {
        try {
            await apiRequest('polls.php', 'POST', { action: 'vote', userId: user.id, pollId, optionId });
            loadPolls(); 
        } catch (e: any) { alert(e.message); }
    };

    if (loading) return null;
    if (polls.length === 0) return null;
    const activePoll = polls.find(p => p.status === 'OPEN') || polls[0];
    if (!activePoll) return null;

    return (
        <Card className="relative overflow-hidden rounded-[1.75rem] border-white/[0.07] bg-gradient-to-br from-[#151923] to-[#0f1119]">
             <div className="absolute top-0 right-0 p-4 opacity-10"><BarChart2 size={80} className="text-blue-500" /></div>
            <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                    <BarChart2 size={20} className="text-blue-500"/>
                    <h3 className="font-bold text-white uppercase text-lg">Community-Umfrage</h3>
                    {activePoll.status === 'CLOSED' && <Badge color="red">Beendet</Badge>}
                    {activePoll.track_location && (
                        <Badge color="yellow" className="ml-auto">
                            <Target size={10} className="mr-1"/> {activePoll.track_location}
                        </Badge>
                    )}
                </div>
                <h4 className="text-md font-bold text-white mb-4">{activePoll.question}</h4>
                {activePoll.userVoted === true ? (
                    <div className="space-y-3">
                        {activePoll.options.map((opt: any) => {
                            const total = parseInt(activePoll.total_votes) || 1;
                            const pct = Math.round((parseInt(opt.votes) / total) * 100);
                            const isChoice = activePoll.userChoice == opt.id;
                            return (
                                <div key={opt.id} className="relative group/poll">
                                    <div className="flex justify-between text-xs text-gray-300 mb-1 z-10 relative">
                                        <span className={isChoice ? 'text-blue-400 font-bold' : ''}>{opt.label} {isChoice && '(Du)'}</span>
                                        <span>{pct}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                        <div style={{width: `${pct}%`}} className={`h-full transition-all duration-1000 ${isChoice ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
                                    </div>
                                    {/* Voter List on Hover */}
                                    {(user?.roles.includes('WEBADMIN') || user?.roles.includes('ADMIN') || user?.roles.includes('STEWARD')) && opt.voters && opt.voters.length > 0 && (
                                        <div className="absolute left-0 top-full mt-1 bg-[#0e0e16] border border-gray-800/40 rounded p-2 text-[10px] text-gray-400 z-50 opacity-0 group-hover/poll:opacity-100 transition-opacity pointer-events-none max-w-xs shadow-2xl">
                                            <div className="font-bold text-gray-500 uppercase mb-1 border-b border-gray-800 pb-1">Stimmen von:</div>
                                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                                {opt.voters.map((v: string, vi: number) => (
                                                    <span key={vi} className="text-gray-300">{v}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <p className="text-[10px] text-gray-500 mt-2 text-right">{activePoll.total_votes} Stimmen</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                         {activePoll.options.map((opt: any) => (
                             <button key={opt.id} onClick={() => handleVote(activePoll.id, opt.id)} disabled={activePoll.status === 'CLOSED'} className="w-full text-left p-3 rounded bg-[#0e0e16] border border-gray-800/40 hover:bg-blue-900/30 hover:border-blue-500 transition-colors text-sm font-bold text-gray-300">
                                 {opt.label}
                             </button>
                         ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

const DotDWidget: React.FC<{ user?: any, onRunoffStatus?: (candidates: string[]) => void }> = ({ user, onRunoffStatus }) => {
    const [status, setStatus] = useState<'loading' | 'active' | 'voted' | 'closed' | 'expired'>('loading');
    const [candidates, setCandidates] = useState<any[]>([]);
    const [eventInfo, setEventInfo] = useState<any>(null);
    const [runoffInfo, setRunoffInfo] = useState<any>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [timeLeft, setTimeLeft] = useState<string>('');
    
    const isWebAdmin = user?.roles.includes(UserRole.WEBADMIN);

    const loadVoteStatus = async () => {
        if(!user) return;
        try {
            const res = await apiRequest<any>(`vote.php?userId=${user.id}`);
            if (res.status === 'no_event' || res.status === 'expired') {
                setStatus('expired');
            } else if (res.status === 'success') {
                setEventInfo({ id: res.eventId, name: res.trackName, deadline: res.deadline });
                setCandidates(res.candidates);
                if (res.isRunoff) {
                    setRunoffInfo(res.runoffInfo);
                    if(onRunoffStatus && res.runoffInfo?.candidates) {
                        onRunoffStatus(res.runoffInfo.candidates);
                    }
                }
                if (res.hasVoted) setStatus('voted');
                else setStatus('active');
            }
        } catch (e) {
            setStatus('expired');
        }
    };

    useEffect(() => { loadVoteStatus(); }, [user]);

    useEffect(() => {
        if (!eventInfo?.deadline) return;
        const tick = () => {
            const now = new Date().getTime();
            const end = new Date(eventInfo.deadline).getTime();
            const diff = end - now;
            if (diff <= 0) { setTimeLeft('Beendet'); } else {
                const h = Math.floor(diff / (1000 * 60 * 60));
                const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const s = Math.floor((diff % (1000 * 60)) / 1000);
                setTimeLeft(`${h}h ${m}m ${s}s`);
            }
        };
        tick();
        const i = setInterval(tick, 1000);
        return () => clearInterval(i);
    }, [eventInfo]);

    const handleVote = async (driverId: string, driverName: string) => {
        if (!window.confirm(`Möchtest du wirklich für '${driverName}' abstimmen?`)) return;
        try {
            await apiRequest('vote.php', 'POST', { userId: user.id, eventId: eventInfo.id, driverId });
            setStatus('voted');
            loadVoteStatus(); 
        } catch (e: any) { alert(e.message); }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    if (status === 'loading') return null;

    if (status === 'expired') {
         return (
            <Card className="relative overflow-hidden rounded-[1.75rem] border-white/[0.07] bg-gradient-to-br from-[#151923] to-[#0f1119] opacity-60 hover:opacity-100 transition-opacity">
                <div className="absolute top-0 right-0 p-4 opacity-5"><Zap size={80} className="text-gray-500" /></div>
                <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-gray-800 p-2 rounded-full"><Zap size={20} className="text-gray-500"/></div>
                        <div>
                            <h3 className="font-bold uppercase text-gray-400">Driver of the Day</h3>
                            <div className="text-xs text-gray-500">Kein aktives Voting</div>
                        </div>
                    </div>
                    <Badge color="gray">CLOSED</Badge>
                </div>
            </Card>
        );
    }

    const totalVotes = candidates.reduce((sum, c) => sum + parseInt(c.votes), 0);
    const isRunoff = !!runoffInfo;
    const initialCount = isRunoff ? 2 : 5;
    const visibleCandidates = isExpanded ? candidates : candidates.slice(0, initialCount);

    return (
        <Card className={`relative overflow-hidden rounded-[1.75rem] border-white/[0.07] bg-gradient-to-br from-[#151923] to-[#0f1119] ${isRunoff ? 'ring-1 ring-red-500/25' : 'ring-1 ring-yellow-500/15'}`}>
            <div className="absolute top-0 right-0 p-4 opacity-10">
                {isRunoff ? <Swords size={80} className="text-red-500"/> : <Zap size={80} className="text-yellow-500" />}
            </div>
            
            <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        {isRunoff ? <Swords className="text-red-500 animate-pulse" size={20}/> : <Zap className="text-yellow-500" size={20} />}
                        <div>
                            <h3 className={`font-bold uppercase text-lg ${isRunoff ? 'text-red-500' : 'text-yellow-500'}`}>
                                {isRunoff ? 'Stichwahl: DotD' : 'Driver of the Day'}
                            </h3>
                            <div className="text-[10px] text-gray-400 font-mono uppercase">{eventInfo?.name}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isWebAdmin && (
                            <Link to="/admin/votes" className="text-gray-500 hover:text-white p-1" title="Voting verwalten">
                                <Settings size={14}/>
                            </Link>
                        )}
                        {(status === 'active' || status === 'voted') && (
                            <div className="flex items-center gap-2 text-xs bg-gray-800 px-2 py-1 rounded text-gray-300 border border-gray-600 font-mono">
                                 <Timer size={12} className="text-yellow-500 animate-pulse"/>
                                 {timeLeft || 'Lädt...'}
                            </div>
                        )}
                    </div>
                </div>

                {status === 'voted' ? (
                    <div className="space-y-3">
                         {candidates.slice(0, 3).map((c, idx) => {
                             const pct = totalVotes > 0 ? Math.round((c.votes / totalVotes) * 100) : 0;
                             return (
                                 <div key={c.driver_id} className="relative">
                                     <div className="flex justify-between items-center text-xs mb-1 z-10 relative">
                                         <span className="font-bold text-white flex items-center gap-2">
                                             {idx === 0 && <Star size={10} className="text-yellow-500 fill-current"/>}
                                             {c.username}
                                         </span>
                                         <span className="font-mono text-gray-400">{pct}%</span>
                                     </div>
                                     <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                         <div style={{width: `${pct}%`, backgroundColor: c.team_color || '#666'}} className="h-full"></div>
                                     </div>
                                 </div>
                             )
                         })}
                         <div className="text-center text-[10px] text-gray-500 mt-2 italic">Danke für deine Stimme!</div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        <div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto pr-1">
                            {visibleCandidates.map(c => {
                                const isSelf = c.driver_id === user.id;
                                return (
                                    <button 
                                        key={c.driver_id}
                                        onClick={() => !isSelf && handleVote(c.driver_id, c.username)}
                                        disabled={isSelf}
                                        className={`flex items-center gap-3 p-2 rounded border transition-all group text-left ${
                                            isSelf 
                                            ? 'bg-gray-900 border-gray-800 opacity-50 cursor-not-allowed' 
                                            : 'bg-gray-900 border-gray-700 hover:bg-gray-800 hover:border-yellow-500 cursor-pointer'
                                        }`}
                                    >
                                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-600 group-hover:border-white shrink-0">
                                            {c.team_logo ? <img src={getUrl(c.team_logo)} className="w-5 h-5 object-contain"/> : <span className="text-xs font-bold text-gray-500">{c.username.charAt(0)}</span>}
                                        </div>
                                        <div>
                                            <div className="font-bold text-sm text-gray-200 group-hover:text-white">
                                                {c.username} {isSelf && <span className="text-[9px] text-gray-500 ml-1">(Du)</span>}
                                            </div>
                                        </div>
                                        {!isSelf && <Vote size={14} className="ml-auto text-gray-600 group-hover:text-yellow-500"/>}
                                    </button>
                                );
                            })}
                        </div>
                        {candidates.length > initialCount && (
                            <button 
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="text-xs text-gray-500 hover:text-white flex items-center justify-center gap-1 py-1 mt-1 transition-colors"
                            >
                                {isExpanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                                {isExpanded ? 'Weniger anzeigen' : `Alle anzeigen (${candidates.length})`}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};

const AdvancedTeammateBattle: React.FC<{ userId: string }> = ({ userId }) => {
    const [data, setData] = useState<any>(null);
    useEffect(() => {
        apiRequest<any>(`teammate_battle.php?userId=${userId}`).then(res => setData(res)).catch(() => {});
    }, [userId]);

    if (!data || !data.mate) return null;

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };
    
    const teamColor = data.team.team_color || '#666';
    const teamLogo = data.team.team_logo;
    const teamCar = data.team.team_car;
    const userAvatar = data.user.avatar_url;
    const mateAvatar = data.mate.avatar_url;

    // Determine overall winner
    const userScore = (data.h2h.race.user || 0) + (data.h2h.quali.user || 0);
    const mateScore = (data.h2h.race.mate || 0) + (data.h2h.quali.mate || 0);

    const ComparisonRow = ({ label, val1, val2 }: { label: string, val1: any, val2: any }) => {
        const v1 = parseFloat(val1) || 0;
        const v2 = parseFloat(val2) || 0;
        const total = v1 + v2;
        const p1 = total > 0 ? (v1 / total) * 100 : 50;
        const win1 = v1 > v2;
        const win2 = v2 > v1;

        return (
            <div className="py-2.5 border-b border-gray-800/20 last:border-0">
                <div className="flex items-center gap-2">
                    <span className="font-mono font-black text-sm w-8 text-right" style={{ color: win1 ? teamColor : '#444' }}>{val1}</span>
                    <div className="flex-1 h-1.5 bg-[#0a0a10] rounded-full flex overflow-hidden">
                        <div style={{width: `${p1}%`, backgroundColor: teamColor, opacity: win1 ? 1 : 0.3}} className="transition-all duration-500 rounded-l-full" />
                        <div style={{width: `${100 - p1}%`, backgroundColor: '#555', opacity: win2 ? 0.6 : 0.15}} className="transition-all duration-500 rounded-r-full" />
                    </div>
                    <span className="font-mono font-black text-sm w-8" style={{ color: win2 ? '#fff' : '#444' }}>{val2}</span>
                </div>
                <div className="text-center text-[8px] font-bold text-gray-600 uppercase tracking-[0.2em] mt-1">{label}</div>
            </div>
        );
    };

    return (
        <div className="relative overflow-hidden rounded-[1.75rem] border border-white/[0.07] bg-gradient-to-br from-[#151923] to-[#0f1119]">
            {/* Top team color accent */}
            <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${teamColor}, ${teamColor}80, transparent)` }} />
            
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-[0.04] blur-3xl pointer-events-none" style={{backgroundColor: teamColor}} />
            {teamLogo && (
                <div className="absolute -right-4 -bottom-4 opacity-[0.03] pointer-events-none">
                    <img src={getUrl(teamLogo)} className="w-36 h-36 object-contain" />
                </div>
            )}

            {/* Header */}
            <div className="px-4 pt-3 pb-2 flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2">
                    <Swords size={13} style={{ color: teamColor }} />
                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/90">Teaminternes Duell</span>
                </div>
                <div className="flex items-center gap-1.5 bg-[#0e0e16] px-2.5 py-1 rounded-lg border border-gray-800/40">
                    {teamLogo && <img src={getUrl(teamLogo)} className="h-4 w-4 object-contain" />}
                    <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: teamColor }}>{data.team.team_name}</span>
                </div>
            </div>

            {/* Driver Portraits */}
            <div className="px-4 py-3 flex items-center justify-between relative z-10">
                {/* User */}
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg overflow-hidden border-2 shrink-0" style={{ borderColor: teamColor }}>
                        {userAvatar ? (
                            <img src={getUrl(userAvatar)} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs font-black" style={{ backgroundColor: teamColor + '20', color: teamColor }}>
                                {(data.user.username || '?').substring(0,2).toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-black text-white truncate leading-none">{data.user.username}</div>
                        <div className="text-[8px] font-bold uppercase tracking-wider mt-0.5" style={{ color: teamColor }}>Du</div>
                    </div>
                </div>

                {/* VS */}
                <div className="px-3 shrink-0">
                    <div className="w-8 h-8 rounded-full border border-gray-800/40 bg-[#0e0e16] flex items-center justify-center">
                        <span className="text-[9px] font-black text-gray-600 italic">VS</span>
                    </div>
                </div>

                {/* Mate */}
                <div className="flex items-center gap-2.5 flex-1 min-w-0 justify-end">
                    <div className="min-w-0 text-right">
                        <div className="text-sm font-black text-gray-400 truncate leading-none">{data.mate.username}</div>
                        <div className="text-[8px] font-bold text-gray-600 uppercase tracking-wider mt-0.5">Teamkollege</div>
                    </div>
                    <div className="w-10 h-10 rounded-lg overflow-hidden border-2 border-gray-700 shrink-0">
                        {mateAvatar ? (
                            <img src={getUrl(mateAvatar)} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center text-xs font-black text-gray-600">
                                {(data.mate.username || '?').substring(0,2).toUpperCase()}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Comparison Bars */}
            <div className="mx-4 mb-3 bg-[#0e0e16] rounded-lg p-3 border border-gray-800/30 relative z-10">
                <ComparisonRow label="Punkte" val1={parseFloat(data.user.points).toFixed(0)} val2={parseFloat(data.mate.points).toFixed(0)} />
                <ComparisonRow label="Rennen H2H" val1={data.h2h.race.user} val2={data.h2h.race.mate} />
                <ComparisonRow label="Quali H2H" val1={data.h2h.quali.user} val2={data.h2h.quali.mate} />
                <ComparisonRow label="Beste Platzierung" val1={data.user.best_pos === 999 || data.user.best_pos === '-' ? '-' : `P${data.user.best_pos}`} val2={data.mate.best_pos === 999 || data.mate.best_pos === '-' ? '-' : `P${data.mate.best_pos}`} />
            </div>

            {/* Car image footer */}
            {teamCar && (
                <div className="h-10 relative overflow-hidden opacity-20 group-hover:opacity-30 transition-opacity">
                    <img src={getUrl(teamCar)} className="absolute bottom-0 right-4 h-14 object-contain" />
                </div>
            )}
        </div>
    );
}

const LiveStreamWidget: React.FC<{ channel: string, nextRace: any }> = ({ channel, nextRace }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isRaceLive, setIsRaceLive] = useState(false);

    useEffect(() => {
        if (nextRace && nextRace.date) {
            const now = new Date().getTime();
            const raceStart = new Date(nextRace.date).getTime();
            const preRaceWindow = 30 * 60 * 1000; 
            const raceDuration = 2.5 * 60 * 60 * 1000; 
            if (now >= (raceStart - preRaceWindow) && now <= (raceStart + raceDuration)) {
                setIsRaceLive(true);
                setIsExpanded(true); 
            }
        }
    }, [nextRace]);
    if (!channel) return null;

    return (
        <div className="mb-6 bg-black border border-purple-900/50 rounded-lg overflow-hidden shadow-2xl transition-all duration-300">
            <div className="bg-purple-900/20 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-purple-900/30 transition-colors" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${isRaceLive ? 'bg-red-600 animate-pulse' : 'bg-purple-700'}`}><Tv size={16} className="text-white"/></div>
                    <div><span className="text-purple-100 font-bold uppercase tracking-wider text-sm block">Official Broadcast</span>{isRaceLive && <span className="text-[10px] text-red-400 font-bold uppercase">● LIVE NOW</span>}</div>
                </div>
                <div className="flex items-center gap-4">
                    <a href={`https://twitch.tv/${channel}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-purple-400 hover:text-white uppercase font-bold hover:underline hidden sm:block">Open Twitch</a>
                    {isExpanded ? <ChevronUp className="text-purple-400"/> : <ChevronDown className="text-purple-400"/>}
                </div>
            </div>
            {isExpanded && <div className="aspect-video w-full bg-black animate-fade-in"><iframe src={`https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}&muted=false`} height="100%" width="100%" allowFullScreen className="w-full h-full"></iframe></div>}
        </div>
    );
};

// UPDATED NOTIFICATION BELL
const NotificationBell: React.FC<{ 
    needsRules: boolean, 
    userId: string,
    isInRunoff: boolean,
    pollCount: number,
}> = ({ needsRules, userId, isInRunoff, pollCount }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [ticketNotifs, setTicketNotifs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<number|null>(null);

    // Fetch specific ticket notifications
    const fetchNotifications = async () => {
        try {
             // action=notifications loads tickets with unread updates
             const res = await apiRequest<any[]>(`tickets.php?action=notifications&userId=${userId}`);
             if (Array.isArray(res)) setTicketNotifs(res);
        } catch(e) { console.error("Notif fetch failed", e); }
    };

    useEffect(() => {
        fetchNotifications();
        intervalRef.current = window.setInterval(fetchNotifications, 10000); // 10s poll
        return () => { if(intervalRef.current) clearInterval(intervalRef.current); };
    }, [userId]);

    const total = (needsRules ? 1 : 0) + (isInRunoff ? 1 : 0) + pollCount + ticketNotifs.length;
    const hasNotifs = total > 0;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={wrapperRef}>
            <button onClick={() => setIsOpen(!isOpen)} className={`p-2 rounded-full transition-colors relative ${hasNotifs ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50 hover:text-red-200 animate-pulse' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                <Bell size={20} />
                {hasNotifs && <span className="absolute -top-1 -right-1 bg-f1-red text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-f1-dark">{total}</span>}
            </button>
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-f1-carbon border border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden animate-fade-in">
                    <div className="p-3 border-b border-gray-700 flex justify-between items-center bg-gray-900">
                        <span className="text-sm font-bold uppercase text-gray-300">Benachrichtigungen</span>
                        <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-white"><X size={14}/></button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {!hasNotifs && <div className="p-4 text-center text-gray-500 text-sm italic">Keine neuen Benachrichtigungen.</div>}
                        
                        {/* POLLS */}
                        {pollCount > 0 && (
                             <div className="block p-3 border-b border-gray-800 bg-blue-900/10 cursor-default">
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-600 p-1.5 rounded text-white animate-pulse"><BarChart2 size={16}/></div>
                                    <div><div className="text-sm font-bold text-blue-400">Offene Umfrage</div><div className="text-xs text-gray-400 mt-1">Deine Meinung ist gefragt! Siehe Dashboard.</div></div>
                                </div>
                            </div>
                        )}
                        
                        {/* RUNOFF */}
                        {isInRunoff && (
                            <div className="block p-3 border-b border-gray-800 bg-red-900/10 cursor-default">
                                <div className="flex items-start gap-3">
                                    <div className="bg-red-600 p-1.5 rounded text-white animate-pulse"><Swords size={16}/></div>
                                    <div><div className="text-sm font-bold text-red-400">Du bist in der Stichwahl!</div><div className="text-xs text-gray-400 mt-1">DotD Voting läuft.</div></div>
                                </div>
                            </div>
                        )}
                        
                        {/* RULES */}
                         {needsRules && (
                            <Link to="/rules" className="block p-3 border-b border-gray-800 hover:bg-gray-800 transition-colors group">
                                <div className="flex items-start gap-3">
                                    <div className="bg-red-900/30 p-1.5 rounded text-f1-red group-hover:text-white group-hover:bg-f1-red transition-colors"><BookOpen size={16}/></div>
                                    <div><div className="text-sm font-bold text-white group-hover:text-f1-red">Regelwerk Update</div><div className="text-xs text-gray-400 mt-1">Bitte akzeptiere das neue Regelwerk.</div></div>
                                </div>
                            </Link>
                        )}
                        
                        {/* TICKET NOTIFICATIONS */}
                        {ticketNotifs.map(t => {
                            // Determine type of update
                            const isNew = !t.last_read_at;
                            const msg = isNew ? "Neues Ticket / Ungelesen" : "Neue Nachricht / Update";
                            const colorClass = t.type === 'PROTEST' ? 'text-red-500' : 'text-yellow-500';
                            
                            return (
                                <Link to={`/tickets`} onClick={() => {/* Trigger reload via nav maybe? */}} key={t.id} className="block p-3 border-b border-gray-800 hover:bg-gray-800 transition-colors group">
                                    <div className="flex items-start gap-3">
                                        <div className={`bg-gray-800 p-1.5 rounded ${colorClass} border border-gray-700 group-hover:border-white transition-colors`}>
                                            <MessageSquare size={16}/>
                                        </div>
                                        <div>
                                            <div className={`text-sm font-bold text-white group-hover:text-f1-red flex items-center gap-2`}>
                                                Ticket #{t.id}
                                                {isNew && <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>}
                                            </div>
                                            <div className="text-xs text-gray-300 font-bold">{t.trackLocation}</div>
                                            <div className="text-xs text-gray-400 mt-1">{msg}</div>
                                            <div className="text-[9px] text-gray-500 mt-1">{new Date(t.updated_at).toLocaleString()}</div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { settings } = useLeague();
  const [season, setSeason] = useState<any>(null);
  const [standings, setStandings] = useState<any[]>([]);
  const [nextRace, setNextRace] = useState<any>(null);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [needsRuleAcceptance, setNeedsRuleAcceptance] = useState(false);
  const [runoffCandidates, setRunoffCandidates] = useState<string[]>([]);
  const [openPollsCount, setOpenPollsCount] = useState(0);
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0, active: false });
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [summaryEvent, setSummaryEvent] = useState<{id: string, location: string} | null>(null);
  const [summaryContent, setSummaryContent] = useState('');
  const [submittingSummary, setSubmittingSummary] = useState(false);
  const [summaryAvailable, setSummaryAvailable] = useState(false);
  const [summaryExpired, setSummaryExpired] = useState(false);
  const [hasSubmittedSummary, setHasSubmittedSummary] = useState(false);
  const navigate = useNavigate(); // Add this
  
  // New States for Widgets
  const [formCurve, setFormCurve] = useState<any[]>([]);
  const [teamRadarTeams, setTeamRadarTeams] = useState<any[]>([]);
  const [userTeamId, setUserTeamId] = useState<string | null>(null);
  const [champions, setChampions] = useState<any>(null);
  const [seasonSignup, setSeasonSignup] = useState<SeasonSignupDashboardData | null>(null);
  const [seasonSignupSaving, setSeasonSignupSaving] = useState<SeasonSignupStatus | null>(null);

  const loadSeasonSignup = async () => {
    if (!user) return;
    try {
      const result = await apiRequest<SeasonSignupDashboardData>(`season_signup.php?userId=${encodeURIComponent(user.id)}`);
      setSeasonSignup(result);
    } catch (error) {
      console.error('Season signup could not be loaded', error);
    }
  };

  // Load next race with UserID to check attendance
  useEffect(() => {
    const eventsEndpoint = user ? `events.php?userId=${user.id}` : 'events.php';
    apiRequest('seasons.php').then(async (res: any) => {
      setSeason(res);
      if (res?.status === 'FINISHED') {
        setNextRace(null);
        return;
      }

      const events = await apiRequest<any[]>(eventsEndpoint);
      if (Array.isArray(events)) {
        const upcoming = events
          .filter((event: any) => event.status !== 'COMPLETED')
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        if (upcoming.length > 0) {
          const next = upcoming[0];
          next.isTestRace = next.isTestRace === true || next.isTestRace === '1';
          setNextRace(next);
        } else {
          setNextRace(null);
        }
      }
    }).catch(() => setNextRace(null));
    apiRequest<{drivers: any[]}>('standings.php').then((res) => { if (res && res.drivers) setStandings(res.drivers); }).catch(() => {});
    apiRequest('season_champions.php').then((res: any) => { if (res?.finished) setChampions(res); }).catch(() => {});
    
    // INCREASED LIMIT TO 8
    apiRequest<NewsItem[]>('news.php?limit=8').then(res => setNews(Array.isArray(res) ? res : [])).catch(()=>{});

    if (user) {
        loadSeasonSignup();
        if (settings.rulebook_updated_at) {
            const rulesDate = new Date(settings.rulebook_updated_at).getTime();
            const userDate = user.rulebook_accepted_at ? new Date(user.rulebook_accepted_at).getTime() : 0;
            if (userDate < rulesDate) setNeedsRuleAcceptance(true);
        }
        
        // --- FETCH DASHBOARD WIDGETS ---
        apiRequest<any>(`dashboard_widgets.php?userId=${user.id}`).then(res => {
            if(res.form) setFormCurve(res.form);
            if(res.radar_teams) setTeamRadarTeams(res.radar_teams);
            if(res.user_team_id) setUserTeamId(res.user_team_id);
        }).catch(()=>{});

        // Check for race summary quote
        apiRequest<{available: boolean, needed: boolean, isExpired: boolean, hasSubmitted: boolean, content: string, event?: {id: string, location: string}}>(`driver_quotes.php?userId=${user.id}`)
            .then(res => {
                if (res.available && res.event) {
                    setSummaryEvent(res.event);
                    setSummaryAvailable(true);
                    setSummaryExpired(res.isExpired);
                    setHasSubmittedSummary(res.hasSubmitted);
                    setSummaryContent(res.content || '');
                    if (res.needed) {
                        setShowSummaryPopup(true);
                    }
                }
            })
            .catch(console.error);
    }
  }, [user, settings]);
  
  // COUNTDOWN
  useEffect(() => {
    if (!nextRace || !nextRace.date) return;
    const timer = setInterval(() => {
        const diff = new Date(nextRace.date).getTime() - Date.now();
        if (diff > 0) {
            const d = Math.floor(diff / (1000 * 60 * 60 * 24));
            const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const s = Math.floor((diff % (1000 * 60)) / 1000);
            setCountdown({ d, h, m, s, active: true });
        } else setCountdown({ d: 0, h: 0, m: 0, s: 0, active: false });
    }, 1000);
    return () => clearInterval(timer);
  }, [nextRace]);

  const isAdmin = user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.WEBADMIN);
  const getImageUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };
  const isInRunoff = user && runoffCandidates.includes(user.id);
  
  const handleAttendance = async (status: AttendanceStatus) => {
      if (!user || !nextRace) return;
      if (needsRuleAcceptance && !isAdmin) {
          alert("Bitte akzeptiere zuerst das Regelwerk (siehe Glocke)!");
          return;
      }
      try {
          // Optimistic update
          setNextRace(prev => ({ ...prev, userAttendance: status }));
          await apiRequest('attendance.php', 'POST', { userId: user.id, eventId: nextRace.id, status });
      } catch(e: any) {
          alert(e.message);
      }
  };

  const handleSeasonSignup = async (response: SeasonSignupStatus) => {
      if (!user || !seasonSignup?.campaign) return;
      setSeasonSignupSaving(response);
      try {
          const result = await apiRequest<{ response: SeasonSignupStatus; counts: SeasonSignupDashboardData['counts'] }>('season_signup.php', 'POST', {
              action: 'respond',
              userId: user.id,
              campaignId: seasonSignup.campaign.id,
              response,
          });
          setSeasonSignup(current => current ? { ...current, response: result.response, counts: result.counts } : current);
      } catch (error: any) {
          alert(error.message || 'Deine Rückmeldung konnte nicht gespeichert werden.');
      } finally {
          setSeasonSignupSaving(null);
      }
  };
  
  // STRIP HTML Helper for News
  const getTextFromHtml = (html: string) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || "";
  };

  const isRegistrationOpen = nextRace && (nextRace.status === 'UPCOMING' || nextRace.status === 'OPEN_FOR_REGISTRATION');

  const handleSubmitSummary = async () => {
    if (!user || !summaryEvent || !summaryContent.trim()) return;
    setSubmittingSummary(true);
    try {
        await apiRequest('driver_quotes.php', 'POST', {
            userId: user.id,
            eventId: summaryEvent.id,
            content: summaryContent,
            action: 'submit'
        });
        setHasSubmittedSummary(true);
        setShowSummaryPopup(false);
    } catch (e) {
        console.error(e);
    } finally {
        setSubmittingSummary(false);
    }
  };

  const handleDismissSummary = async () => {
    if (!user || !summaryEvent) return;
    try {
        await apiRequest('driver_quotes.php', 'POST', {
            userId: user.id,
            eventId: summaryEvent.id,
            action: 'dismiss'
        });
        setShowSummaryPopup(false);
    } catch (e) {
        console.error(e);
    }
  };

  const userStandingIndex = user
    ? standings.findIndex((driver: any) => String(driver.id) === String(user.id))
    : -1;
  const userStanding = userStandingIndex >= 0 ? standings[userStandingIndex] : null;
  const notificationCount = (needsRuleAcceptance ? 1 : 0) + (isInRunoff ? 1 : 0) + openPollsCount + (seasonSignup?.campaign && !seasonSignup.response ? 1 : 0);
  const seasonFinished = season?.status === 'FINISHED';
  const dashboardStats = [
    {
      label: 'WM-Position',
      value: userStandingIndex >= 0 ? `P${userStandingIndex + 1}` : '–',
      detail: userStanding ? `${Number(userStanding.totalPoints || 0).toFixed(0)} Punkte` : 'Noch nicht gewertet',
      icon: Trophy,
    },
    {
      label: seasonFinished ? 'Saisonstatus' : 'Nächstes Rennen',
      value: seasonFinished ? 'ENDE' : countdown.active ? `${countdown.d}T ${countdown.h}H` : nextRace ? 'LIVE' : '–',
      detail: seasonFinished ? 'Saison abgeschlossen' : nextRace?.trackLocation || 'Noch nicht geplant',
      icon: Calendar,
    },
    {
      label: 'Offene Aktionen',
      value: String(notificationCount),
      detail: notificationCount > 0 ? 'Aufmerksamkeit nötig' : 'Alles erledigt',
      icon: Bell,
    },
  ];


  return (
    <div className="mx-auto space-y-5 animate-fade-in pb-16 lg:space-y-6">
      <section className="modern-dashboard-intro relative overflow-hidden">
        <div className="relative z-10 grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(28rem,0.8fr)] xl:items-center">
          <div className="max-w-3xl">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-f1-red/30 bg-f1-red/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-100">
                <span className="h-1.5 w-1.5 rounded-full bg-f1-red shadow-[0_0_12px_rgba(239,18,8,0.9)]" />
                {seasonFinished ? 'Saison abgeschlossen' : 'SDC Race Control'}
              </span>
              {season && <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">{season.name} · {season.year}</span>}
            </div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-white/40">Willkommen zurück, {user?.username}</p>
            <h1 className="sdc-display max-w-3xl text-[2.35rem] text-white sm:text-5xl lg:text-[3.5rem]">
              {seasonFinished ? <>Die Saison ist <span className="text-f1-red">beendet.</span></> : <>Dein Rennen beginnt <span className="text-f1-red">hier.</span></>}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-white/55">
              {seasonFinished
                ? 'Alle Ergebnisse, Tabellen und Saisonstatistiken bleiben weiterhin vollständig abrufbar.'
                : nextRace
                ? `${nextRace.trackLocation} steht als Nächstes an. Anmeldung, Meisterschaft und alle wichtigen Liga-Aktionen auf einen Blick.`
                : 'Saisonstatus, Meisterschaft und alle wichtigen Liga-Aktionen auf einen Blick.'}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {dashboardStats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="modern-stat-card group p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-[0.18em] text-white/35">{stat.label}</span>
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/[0.07] bg-white/[0.04] text-white/45 transition-colors group-hover:text-f1-red">
                      <Icon size={15} />
                    </span>
                  </div>
                  <div className="font-mono text-3xl font-black tracking-tight text-white">{stat.value}</div>
                  <div className="mt-1 truncate text-[10px] font-semibold uppercase tracking-wider text-white/35">{stat.detail}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {seasonSignup?.campaign && (
        <section className="relative overflow-hidden rounded-[2rem] border border-f1-red/25 bg-[linear-gradient(118deg,rgba(239,18,8,.20),rgba(18,20,28,.98)_45%,rgba(10,11,16,.98))] p-5 shadow-[0_26px_75px_rgba(0,0,0,.28)] md:p-7">
          <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-f1-red/10 blur-3xl"/>
          <Megaphone className="pointer-events-none absolute -right-3 bottom-[-2rem] h-40 w-40 rotate-[-10deg] text-f1-red opacity-[0.035]" aria-hidden="true"/>
          <div className="relative grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(32rem,.9fr)] xl:items-center">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-f1-red/30 bg-f1-red/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-red-100">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-f1-red shadow-[0_0_12px_rgba(239,18,8,.9)]"/> Anmeldung geöffnet
                </span>
                {seasonSignup.campaign.deadline && <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/35">Antwort bis {new Date(`${seasonSignup.campaign.deadline}T12:00:00`).toLocaleDateString('de-DE')}</span>}
              </div>
              <p className="mt-5 text-[10px] font-black uppercase tracking-[0.24em] text-f1-red">Fahrerplanung</p>
              <h2 className="sdc-display mt-2 text-4xl text-white sm:text-5xl">Bist du bei {seasonSignup.campaign.season_name} dabei?</h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/55">Deine Rückmeldung hilft der Ligaleitung bei der Grid- und Teamplanung. Du kannst deine Auswahl bis zum Ende der Abfrage jederzeit ändern.</p>
              {seasonSignup.response && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-300">
                  <CheckCircle size={15}/> Deine Antwort ist gespeichert.
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { status: 'MAIN' as const, label: 'Stammfahrer', description: 'Fester Platz im Grid', icon: UserCheck, active: 'border-emerald-400/60 bg-emerald-500/15 text-emerald-200 shadow-emerald-950/30', iconClass: 'bg-emerald-500/15 text-emerald-300' },
                { status: 'RESERVE' as const, label: 'Ersatzfahrer', description: 'Flexibel verfügbar', icon: UserMinus, active: 'border-blue-400/60 bg-blue-500/15 text-blue-200 shadow-blue-950/30', iconClass: 'bg-blue-500/15 text-blue-300' },
                { status: 'OUT' as const, label: 'Nicht dabei', description: 'Diese Saison Pause', icon: UserX, active: 'border-gray-400/50 bg-white/10 text-white shadow-black/30', iconClass: 'bg-white/[0.07] text-gray-300' },
              ].map(option => {
                const selected = seasonSignup.response === option.status;
                const busy = seasonSignupSaving === option.status;
                return (
                  <button
                    key={option.status}
                    onClick={() => handleSeasonSignup(option.status)}
                    disabled={seasonSignupSaving !== null}
                    className={`group relative min-h-[9.5rem] rounded-[1.4rem] border p-4 text-left shadow-xl transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 disabled:cursor-wait disabled:opacity-70 ${selected ? option.active : 'border-white/[0.08] bg-black/20 text-white/75'}`}
                  >
                    <span className={`grid h-11 w-11 place-items-center rounded-[1rem] ${option.iconClass}`}>{busy ? <Loader2 className="animate-spin" size={20}/> : <option.icon size={20}/>}</span>
                    <span className="mt-5 block text-sm font-black">{option.label}</span>
                    <span className="mt-1 block text-[10px] font-semibold uppercase tracking-wider text-white/35">{option.description}</span>
                    {selected && <CheckCircle className="absolute right-4 top-4 text-current" size={17}/>} 
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/[0.07] pt-4 text-[10px] font-bold uppercase tracking-wider text-white/30">
            <span><strong className="text-emerald-300">{seasonSignup.counts.MAIN}</strong> Stammfahrer</span>
            <span><strong className="text-blue-300">{seasonSignup.counts.RESERVE}</strong> Ersatzfahrer</span>
            <span><strong className="text-gray-300">{seasonSignup.counts.OUT}</strong> nicht dabei</span>
            <span><strong className="text-amber-300">{seasonSignup.counts.PENDING}</strong> Antworten offen</span>
          </div>
        </section>
      )}

      {/* Race Summary Popup */}
      {showSummaryPopup && summaryEvent && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
              <div className="bg-[#12121a] border border-gray-800/60 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
                  <div className="h-[2px] bg-gradient-to-r from-f1-red to-transparent" />
                  <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                          <h3 className="font-black uppercase italic text-white text-lg flex items-center gap-2"><MessageSquare size={18} className="text-f1-red"/> Renn-Statement</h3>
                          <button onClick={() => setShowSummaryPopup(false)} className="text-gray-600 hover:text-white transition-colors"><X size={18}/></button>
                      </div>
                      <p className="text-xs text-gray-400 mb-4">Wie war das Rennen in <strong className="text-white">{summaryEvent.location}</strong> für dich?</p>
                      <textarea className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-3 text-sm text-white placeholder:text-gray-700 resize-none focus:border-f1-red outline-none h-28" placeholder="z.B. 'Starker Start, dann leider Reifenabbau...'" value={summaryContent} onChange={e => setSummaryContent(e.target.value)} maxLength={2000} />
                      <div className="flex justify-between items-center mt-1 mb-4"><span className="text-[10px] text-gray-600">{summaryContent.length}/2000</span></div>
                      <div className="flex gap-2">
                          <Button variant="primary" onClick={handleSubmitSummary} disabled={submittingSummary || !summaryContent.trim()} className="flex-1">{submittingSummary ? 'Speichert...' : hasSubmittedSummary ? 'Aktualisieren' : 'Abschicken'}</Button>
                          <Button variant="ghost" onClick={handleDismissSummary}>Später</Button>
                      </div>
                      <p className="text-[10px] text-gray-600 text-center mt-3 uppercase tracking-wider">Dieses Fenster erscheint für 24h nach dem Rennen.</p>
                  </div>
              </div>
          </div>
      )}

      {settings.twitch_channel && <LiveStreamWidget channel={settings.twitch_channel} nextRace={nextRace} />}

      {summaryAvailable && summaryEvent && (
          summaryExpired ? (
              <div className="flex justify-end">
                  <button onClick={() => setShowSummaryPopup(true)} className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-gray-600 hover:text-white transition-colors bg-[#0e0e16] px-3 py-1.5 rounded-lg border border-gray-800/40">
                      <MessageSquare size={10} />{hasSubmittedSummary ? `Statement: ${summaryEvent.location}` : `Statement nachreichen: ${summaryEvent.location}`}
                  </button>
              </div>
          ) : (
              <div className="bg-[#12121a] border border-gray-800/40 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${hasSubmittedSummary ? 'bg-green-500/10 border border-green-500/20' : 'bg-f1-red/10 border border-f1-red/20'}`}>
                          <MessageSquare size={16} className={hasSubmittedSummary ? 'text-green-500' : 'text-f1-red'} />
                      </div>
                      <div>
                          <h4 className="text-sm font-bold text-white uppercase italic">Renn-Statement: {summaryEvent.location}</h4>
                          <p className="text-[10px] text-gray-500">{hasSubmittedSummary ? 'Gespeichert.' : 'Noch kein Statement abgegeben.'}</p>
                      </div>
                  </div>
                  <Button size="sm" variant={hasSubmittedSummary ? 'secondary' : 'primary'} onClick={() => setShowSummaryPopup(true)} className="w-full md:w-auto">{hasSubmittedSummary ? 'Bearbeiten' : 'Jetzt eintragen'}</Button>
              </div>
          )
      )}

      {/* Champions Celebration */}
      {champions && champions.finished && champions.wm_decided && (
        <div className="relative overflow-hidden rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 via-[#12121a] to-[#12121a]">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIgZmlsbD0iI2ZiYmYyNDAzIi8+PC9zdmc+')] opacity-30"/>
          <div className="relative z-10 p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="text-[10px] text-yellow-500/60 font-bold uppercase tracking-[0.4em] mb-1">{champions.season_name}</div>
              <h2 className="text-2xl md:text-3xl font-black uppercase italic text-white tracking-tight">Saison-Gewinner</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* WM */}
              {champions.wm_top3?.length > 0 && (
                <div className="bg-black/20 rounded-xl p-5 backdrop-blur-sm border border-yellow-500/10">
                  <div className="text-[9px] text-yellow-500 font-bold uppercase tracking-[0.3em] mb-4 text-center">🏆 Fahrer-Weltmeister</div>
                  <div className="space-y-2.5">
                    {champions.wm_top3.map((d: any, i: number) => (
                      <div key={d.id} className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black shrink-0 ${i===0?'bg-gradient-to-br from-yellow-400 to-amber-600 text-black':i===1?'bg-gradient-to-br from-gray-300 to-gray-500 text-black':'bg-gradient-to-br from-orange-400 to-orange-700 text-black'}`}>{i+1}</div>
                        <div className="w-0.5 h-6 rounded-full shrink-0" style={{backgroundColor: d.team_color || '#666'}}/>
                        <div className="flex-1 min-w-0">
                          <div className={`font-black uppercase text-sm truncate ${i===0?'text-white':'text-white/70'}`}>{d.username}</div>
                          <div className="text-[9px] text-white/25">{d.team_name}</div>
                        </div>
                        <div className={`font-mono font-black text-right w-12 shrink-0 ${i===0?'text-yellow-400 text-base':'text-white/80 text-sm'}`}>{parseFloat(d.total_points).toFixed(0)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* KWM */}
              {champions.kwm_top3?.length > 0 && (
                <div className="bg-black/20 rounded-xl p-5 backdrop-blur-sm border border-yellow-500/10">
                  <div className="text-[9px] text-yellow-500 font-bold uppercase tracking-[0.3em] mb-4 text-center">🏆 Konstrukteurs-Weltmeister</div>
                  <div className="space-y-2.5">
                    {champions.kwm_top3.map((t: any, i: number) => (
                      <div key={t.id} className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black shrink-0 ${i===0?'bg-gradient-to-br from-yellow-400 to-amber-600 text-black':i===1?'bg-gradient-to-br from-gray-300 to-gray-500 text-black':'bg-gradient-to-br from-orange-400 to-orange-700 text-black'}`}>{i+1}</div>
                        <div className="w-0.5 h-6 rounded-full shrink-0" style={{backgroundColor: t.color || '#666'}}/>
                        <div className="flex-1 min-w-0">
                          <div className={`font-black uppercase text-sm truncate ${i===0?'':'text-white/70'}`} style={{color: i===0 ? t.color : undefined}}>{t.name}</div>
                        </div>
                        <div className={`font-mono font-black text-right w-12 shrink-0 ${i===0?'text-yellow-400 text-base':'text-white/80 text-sm'}`}>{parseFloat(t.total_points).toFixed(0)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Season Header */}
      <div className="flex flex-col items-start justify-between gap-4 rounded-[1.35rem] border border-white/[0.07] bg-white/[0.025] p-4 md:flex-row md:items-center md:px-5">
        <div className="flex items-center gap-4">
            {season ? (<>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-f1-red/20 bg-f1-red/10 text-f1-red"><Flag size={18} /></div>
                <div>
                    <div className="mb-0.5 text-[9px] font-black uppercase tracking-[0.2em] text-white/30">Aktive Meisterschaft</div>
                    <h2 className="text-base font-black uppercase tracking-tight text-white md:text-lg">{season.name}</h2>
                    <div className="mt-0.5 flex items-center gap-2">
                        <div className={`h-1.5 w-1.5 rounded-full ${season.status === 'PUBLISHED' ? 'bg-emerald-400' : 'bg-yellow-400'}`} />
                        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/35">Saison {season.year} · {season.status === 'PUBLISHED' ? 'Live' : 'Vorbereitung'}</span>
                    </div>
                </div>
            </>) : (
                <div><h1 className="text-2xl font-black italic text-gray-600">Keine aktive Saison</h1>{isAdmin && <Link to="/admin/wizard" className="text-f1-red text-xs underline">Assistent starten</Link>}</div>
            )}
            {user && <div className="ml-2"><NotificationBell needsRules={needsRuleAcceptance} userId={user.id} isInRunoff={!!isInRunoff} pollCount={openPollsCount} /></div>}
        </div>
        <div className="flex gap-2 w-full md:w-auto">
            {isAdmin && <Link to="/admin/seasons" className="flex-1 md:flex-none"><Button variant="secondary" icon={History} className="w-full text-xs">Saisons</Button></Link>}
            <Link to="/calendar" className="flex-1 md:flex-none"><Button variant="secondary" icon={Calendar} className="w-full text-xs">Kalender</Button></Link>
        </div>
      </div>

      {/* Tippspiel Banner */}
      <Link to="/predictions" className="group relative block overflow-hidden rounded-[1.5rem] border border-violet-400/15 bg-[linear-gradient(110deg,rgba(95,45,210,0.24),rgba(23,26,42,0.84)_52%,rgba(255,255,255,0.025))] p-5 transition-all hover:-translate-y-0.5 hover:border-violet-400/35 hover:shadow-[0_22px_55px_rgba(40,20,90,0.22)] md:px-6">
         <div className="absolute -right-8 -top-16 h-40 w-40 rounded-full bg-violet-500/15 blur-3xl transition-transform group-hover:scale-125" />
         <div className="relative flex items-center justify-between gap-4">
             <div className="flex items-center gap-4">
                 <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-violet-300/15 bg-violet-400/10 transition-transform group-hover:rotate-3 group-hover:scale-105"><Target size={21} className="text-violet-300"/></div>
                 <div><div className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-violet-300/55">Prediction League</div><h3 className="text-lg font-black uppercase tracking-tight text-white">Dein Grid-Tipp wartet</h3><p className="mt-0.5 text-[11px] font-semibold text-white/40">Top 10 tippen, Punkte sammeln, Liga schlagen.</p></div>
             </div>
             <span className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-white/70 transition-colors group-hover:bg-white group-hover:text-black sm:flex">Jetzt tippen <ArrowRight size={13}/></span>
         </div>
      </Link>

      {/* Next Race + Top 3 */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="relative flex min-h-[390px] flex-col justify-center overflow-hidden rounded-[2rem] border border-white/[0.1] bg-[#11141d] shadow-[0_30px_80px_rgba(0,0,0,0.3)] lg:col-span-2">
            {nextRace ? (<>
                    <div className="absolute inset-0 z-10 bg-[linear-gradient(100deg,rgba(7,8,13,0.98)_5%,rgba(239,18,8,0.66)_48%,rgba(7,8,13,0.78)_100%)]" />
                    <div className="absolute inset-0 z-0 scale-105 bg-cover bg-center opacity-35 saturate-50" style={{backgroundImage: `url(${getImageUrl(nextRace.trackFlag)})`}} />
                    <div className="absolute inset-y-0 right-[13%] z-10 hidden w-px rotate-[18deg] bg-white/10 md:block" />
                    <div className="relative z-20 flex h-full flex-col justify-between p-6 md:p-8 lg:p-10">
                        <div className="flex justify-between items-start">
                            {nextRace.isTestRace ? <Badge className="bg-blue-900/80 text-blue-200 border-blue-500/50 text-[10px]">TEST RACE</Badge> : <Badge className="border-white/15 bg-black/20 px-3 py-1.5 text-[9px] font-black tracking-[0.2em] text-white backdrop-blur-md">NEXT RACE</Badge>}
                            <div className="text-right">
                                {countdown.active ? (
                                    <div className="hidden gap-2 sm:flex">
                                        {[{val:countdown.d,label:'Tage'},{val:countdown.h,label:'Std'},{val:countdown.m,label:'Min'},{val:countdown.s,label:'Sek'}].map((u,i) => (
                                            <div key={i} className="min-w-[48px] rounded-xl border border-white/10 bg-black/25 px-2.5 py-2 text-center backdrop-blur-md">
                                                <span className={`font-mono text-xl font-black leading-none text-white ${i===3?'text-yellow-300':''}`}>{String(u.val).padStart(2, '0')}</span>
                                                <div className="mt-1 text-[7px] font-bold uppercase tracking-wider text-white/35">{u.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="text-sm font-black italic uppercase text-red-500 bg-white px-2 py-1 rounded animate-pulse">LIVE</div>}
                            </div>
                        </div>
                        <div className="mt-16 md:mt-20">
                            <div className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/45">{new Date(nextRace.date).toLocaleDateString('de-DE')} · {new Date(nextRace.date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                            <h2 className="sdc-display mb-3 max-w-3xl text-5xl text-white md:text-7xl lg:text-[5.2rem]">{nextRace.trackLocation}</h2>
                            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-white/55">{nextRace.trackName}</p>
                            {user && isRegistrationOpen && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button onClick={() => handleAttendance(AttendanceStatus.ATTENDING)} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold uppercase text-xs border transition-all ${nextRace.userAttendance === 'ATTENDING' ? 'bg-green-600 text-white border-green-500' : 'bg-black/30 text-gray-300 border-white/10 hover:bg-green-600/40 hover:text-white'}`}><CheckCircle size={14}/> Dabei</button>
                                    <button onClick={() => handleAttendance(AttendanceStatus.ABSENT)} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold uppercase text-xs border transition-all ${nextRace.userAttendance === 'ABSENT' ? 'bg-red-600 text-white border-red-500' : 'bg-black/30 text-gray-300 border-white/10 hover:bg-red-600/40 hover:text-white'}`}><XCircle size={14}/> Fehle</button>
                                    <Link to="/calendar" className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-bold uppercase transition-colors border border-white/5">Details</Link>
                                </div>
                            )}
                            <div className="mt-4 flex items-center gap-2 text-[11px] font-mono text-white/50 sm:hidden">
                                {countdown.active ? `Noch ${countdown.d} Tage, ${countdown.h} Stunden` : 'Rennen läuft'}
                                {nextRace.isSprint && <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30 text-[9px]">SPRINT</Badge>}
                            </div>
                        </div>
                    </div>
            </>) : <div className="flex items-center justify-center h-full text-gray-600 flex-col gap-2 py-16"><Flag size={36} className="opacity-20"/><div className="text-sm font-bold">{season ? 'Saison beendet!' : 'Erstelle eine Saison.'}</div></div>}
        </div>

        {/* Top 3 */}
        <div className="sdc-panel flex flex-col overflow-hidden rounded-[2rem]">
            <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-5">
                <div><div className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-yellow-500/55">Championship</div><div className="flex items-center gap-2"><Trophy size={15} className="text-yellow-400" /><span className="text-sm font-black uppercase tracking-tight text-white">Spitze des Feldes</span></div></div>
                <Link to="/standings" className="text-[9px] font-bold text-f1-red uppercase tracking-wider hover:underline">Alle →</Link>
            </div>
            <div className="flex flex-1 flex-col justify-center p-4">
                {standings.length > 0 ? standings.slice(0, 3).map((driver, idx) => (
                    <Link to={`/driver/${driver.id}`} key={driver.id || idx} className="group flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-white/[0.04]">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-base font-black ${idx===0?'bg-gradient-to-br from-yellow-300 to-amber-600 text-black shadow-[0_10px_30px_rgba(245,158,11,0.2)]':idx===1?'bg-gradient-to-br from-gray-200 to-gray-500 text-black':'bg-gradient-to-br from-orange-300 to-orange-700 text-black'}`}>{idx+1}</div>
                        <div className="h-8 w-1 shrink-0 rounded-full" style={{backgroundColor: driver.teamColor || '#666'}} />
                        <div className="min-w-0 flex-1"><div className="truncate text-sm font-black uppercase text-white transition-colors group-hover:text-f1-red">{driver.username}</div><div className="truncate text-[9px] font-semibold uppercase tracking-wider text-white/25">{driver.teamName || 'Privatfahrer'}</div></div>
                        <div className="text-right"><span className="font-mono text-lg font-black text-white">{driver.totalPoints}</span><div className="text-[8px] font-bold uppercase tracking-wider text-white/25">Punkte</div></div>
                    </Link>
                )) : <div className="text-gray-600 text-center py-8 text-xs">Noch keine Daten.</div>}
            </div>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid min-h-[230px] grid-cols-1 gap-5 md:grid-cols-2">
          <FormCurveWidget data={formCurve} />
          <TeamRadarWidget teamsData={teamRadarTeams} userTeamId={userTeamId} />
      </div>

      {/* News + Sidebar */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        <div className="sdc-panel flex flex-col overflow-hidden rounded-[2rem] md:col-span-2">
            <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-5">
                <div><div className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-f1-red/60">Paddock Feed</div><div className="flex items-center gap-2"><Newspaper size={15} className="text-white/40" /><span className="text-sm font-black uppercase tracking-tight text-white">Aktuelle News</span></div></div>
                <Link to="/news" className="text-[9px] font-bold text-f1-red uppercase tracking-wider hover:underline flex items-center gap-1">Alle lesen <ArrowRight size={10}/></Link>
            </div>
            <div className="custom-scrollbar max-h-[500px] flex-1 space-y-1 overflow-y-auto p-4 md:p-5">
                {news.length > 0 ? news.map(n => (
                    <div key={n.id} onClick={() => navigate('/news')} className="group flex cursor-pointer items-start gap-4 rounded-2xl border-b border-white/[0.04] p-4 transition-colors last:border-0 hover:bg-white/[0.035]">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.025] text-[10px] font-black text-white/30 transition-colors group-hover:border-f1-red/20 group-hover:text-f1-red">{String(new Date(n.created_at).getDate()).padStart(2, '0')}</div>
                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-2">
                                <h3 className="font-bold text-white text-sm group-hover:text-f1-red transition-colors truncate">{n.title}</h3>
                                <span className="text-[9px] text-gray-600 shrink-0 font-mono">{new Date(n.created_at).toLocaleDateString('de-DE')}</span>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{getTextFromHtml(n.content)}</p>
                        </div>
                    </div>
                )) : <div className="text-gray-600 text-center py-8 text-xs">Keine Neuigkeiten.</div>}
            </div>
        </div>
        <div className="space-y-5">
            <PollWidget user={user} onUnvoted={setOpenPollsCount} />
            <DotDWidget user={user} onRunoffStatus={setRunoffCandidates} />
            {user && <AdvancedTeammateBattle userId={user.id} />}
        </div>
      </div>
    </div>
  );
};
